import {
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Button,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  GlobalRecurringDateConfig,
  RecurringDateSetupType,
  SourceType,
} from '../EditRecurringDateModal';
import { colors } from 'packages/common-frontend/colors';
import {
  formatSelectOptions,
  Select,
} from 'packages/common-frontend/components/Select';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export const createStaticRecurringDateSetupShell = (): CreditAgreement.Config.GlobalRecurringDate.StaticRecurringDateSetup => {
  return {
    type: 'static',
    config: {
      recurringDateConfigs: [
        {
          startDate: commonDate.currentDateStr(),
          endDate: commonDate.currentDateStr(),
          type: 'startOf',
          period: 'month',
          cadence: 1,
        },
      ],
      exclusions: [],
    },
  };
};

export const createRelativeRecurringDateSetupShell = (params: {
  source: SourceType;
}): CreditAgreement.Config.GlobalRecurringDate.RelativeRecurringDateSetup => {
  const { source } = params;
  const defaultFormula =
    source === 'reporting' ? 'reportingDate' : 'triggerDate';
  return {
    type: 'relative',
    triggerDateFormula: defaultFormula,
    config: {
      relativeRecurringDateConfig: [
        {
          startDateFormula: 'EOMONTH(triggerDate,-1)',
          endDateFormula: 'EOMONTH(triggerDate,1)',
          baseDateConfig: {
            type: 'startOf',
            period: 'month',
            cadence: 1,
          },
        },
      ],
      exclusions: [],
    },
  };
};

export const createRecurringDateConfigShell = ({
  recurringDateFieldKey,
  setupType,
  source,
}: {
  recurringDateFieldKey: string;
  setupType: RecurringDateSetupType;
  source: SourceType;
}): GlobalRecurringDateConfig => {
  switch (setupType) {
    case 'static':
      return {
        field: recurringDateFieldKey,
        setup: createStaticRecurringDateSetupShell(),
        maxCount: 5,
        defaultTo: { value: '', parseString: false },
      };
    case 'relative':
      return {
        field: recurringDateFieldKey,
        setup: createRelativeRecurringDateSetupShell({ source }),
        maxCount: 5,
        defaultTo: { value: '', parseString: false },
      };
    default:
      return {
        field: recurringDateFieldKey,
        setup: createStaticRecurringDateSetupShell(),
        maxCount: 5,
        defaultTo: { value: '', parseString: false },
      };
  }
};

export const AddGlobalRecurringDateModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onClickAdd: ({
    setupTypeInput,
    recurringDateFieldKeyInput,
  }: {
    setupTypeInput: RecurringDateSetupType;
    recurringDateFieldKeyInput: string;
  }) => void;
  initialRecurringDateFieldKey: string;
  initialSetupType: RecurringDateSetupType;
  isAddLoading: boolean;
  mode: 'add' | 'duplicate';
}> = ({
  isOpen,
  onClose,
  onClickAdd,
  initialRecurringDateFieldKey,
  initialSetupType,
  isAddLoading,
  mode,
}) => {
  const toast = useToast();

  const [setupType, setSetupType] = useState<
    RecurringDateSetupType | undefined
  >(initialSetupType);
  const [recurringDateFieldKey, setRecurringDateFieldKey] = useState<
    string | undefined
  >(initialRecurringDateFieldKey);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {`${commonUtils.toTitleCase(mode)} Recurring Date`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb="16px" fontWeight={600}>
            Setup Type
          </Text>
          <Select
            value={setupType}
            onChange={(newValue) => setSetupType(newValue)}
            options={formatSelectOptions(
              CreditAgreement.Config.GlobalRecurringDate.SetupTypeIterable,
              'title',
            )}
            isDisabled={mode === 'duplicate'} // when duplicating, will keep everything aside from the key
          />
          <Text mt="8px" fontSize="14px" color={colors.black[60]}>
            This cannot be changed later.
          </Text>
          <Text mt="24px" mb="16px" fontWeight={600}>
            Key
          </Text>
          <Input
            value={recurringDateFieldKey}
            onChange={
              (event) =>
                setRecurringDateFieldKey(_.camelCase(event.target.value)) // only allow camelCase covenantKey
            }
          />
          <Text mt="8px" fontSize="14px" color={colors.black[60]}>
            Auto-formatted in camelCase. This cannot be changed later.
          </Text>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            color="white"
            onClick={() => {
              if (setupType && recurringDateFieldKey) {
                onClickAdd({
                  setupTypeInput: setupType,
                  recurringDateFieldKeyInput: recurringDateFieldKey,
                });
              } else {
                toast({
                  ...errorToastConfig,
                  id: 'recurring-date-config-missing-info',
                  title: 'Fill in all required fields',
                });
              }
            }}
            isLoading={isAddLoading}
          >
            {`${commonUtils.toTitleCase(mode)} Recurring Date Configuration`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
